import React, { useContext, useEffect, useState } from 'react';
import { css } from '@emotion/css';
import appLabels from '../../utils/appLabels';
import formatString from '../../utils/formatString';
import OrderTile from '../Order/OrderTile';
import SkipOrderLink from '../ScheduledItems/SkipOrderLink';
import { first, get, invoke, isEmpty, curry, range } from 'lodash';
import { useApp } from '../../context/app-context';
import UserMessage from '../../common/UserMessage/UserMessage';
import PropTypes from 'prop-types';
import { ItemsContext, ItemsContextType } from '../ScheduledItems/ItemsContext';
import ExpandOrderButton from '../Order/ExpandOrderButton';
import PaymentErrorMessageComponent from '../../common/PaymentErrorMessage/PaymentErrorMessage';

const globalStyle = css`
  margin-top: 1.5rem;
`;

const messageStyle = css`
  margin-bottom: 1rem;
`;

const ageRestrictedMsgStyle = css`
  margin-bottom: 1rem;
`;

const mainStyle = css`
  display: flex;
  flex-direction: column;
`;

const firstSectionStyle = css`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const futureSectionStyle = css`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const futureTextContainer = css`
  display: flex;
`;

const futureExpandButtonContainer = css`
  margin-right: 0.8rem;
`;

const titleStyle = css`
  font-weight: 600;
  font-size: 1.25rem;
  line-height: 1.5rem;
`;

const secondSectionStyle = css`
  display: flex;
  flex-direction: column;
  margin-top: 1.5rem;
`;

const subTitleStyle = css`
  font-weight: 400;
  font-size: 0.75rem;
  line-height: 1.125rem;
  display: flex;
  align-items: center;
  margin-left: 0.5rem;
`;

const futureOrderStyle = css`
  margin-bottom: 0;
  @media (max-width: 641px) {
    margin-right: 0;
    margin-bottom: 1rem;
  }
`;

const futureOrderListStyle = css`
  display: flex;
  flex-direction: column;
  overflow: auto;
  gap: 1rem;
`;

type FutureOrder = {
  orderDate: string;
  numberOfItems: number;
  estimatedSubTotal: number;
  orderEditDate: string;
  fulfillmentType: string;
};

const UserWithActiveOrders = ({
  upcomingOrders,
  futureOrders,
  isApiError,
  isFutureApiError,
  isApiLoading,
  isFutureApiLoading,
  hasRestrictedItems,
  preferencesData,
}) => {
  const upcomingOrder = get(upcomingOrders, 'upcomingOrder');
  const paymentFailure = get(upcomingOrders, 'preCheckFailReason') === 'PAYMENT_FAILURE';

  const futureOrderList: FutureOrder[] = get(futureOrders, 'futureOrders', []);
  const hasFutureOrders = !isEmpty(futureOrderList);

  const [expanded, setExpanded] = useState(true);
  const [expandedFutureOrder, setExpandedFutureOrder] = useState(-1);

  const orderID: string | null = get(upcomingOrders, 'upcomingOrder.orderID', null);
  const showSkipLink =
    (!orderID || orderID === 'null') && /preparing/i.test(get(upcomingOrders, 'upcomingOrder.orderStatus', ''));

  const orderDate = get(upcomingOrders, 'upcomingOrder.orderDate', '');
  const nextOrderDate: string = get(first(futureOrderList), 'orderDate', '');

  const showNextOrderText = formatString(appLabels.UserWithActiveOrders.showNextText, {
    orderList: hasFutureOrders ? Math.min(futureOrderList.length, 3) : 0,
    multiItem: hasFutureOrders ? (futureOrderList.length > 1 ? 's' : '') : '',
  });

  const { displaySlotChangesMessage, firstTabBannerMessage } = useApp();

  const externalStyle = `${mainStyle} ${globalStyle}`;

  const currentItemsContext = useContext(ItemsContext) as ItemsContextType;

  useEffect(() => {
    invoke(currentItemsContext, 'fetchItemsInfo');
  }, []);

  const handleFutureOrderExpand = (index: number, value: boolean) => {
    if (expandedFutureOrder == index && !value) {
      setExpandedFutureOrder(-1);
    } else if (value) {
      setExpandedFutureOrder(index);
    }
  };

  return (
    <div className={externalStyle}>
      <div>
        <div className={firstSectionStyle}>
          <div className={titleStyle}>{appLabels.UserWithActiveOrders.nextOrderText}</div>
          {showSkipLink && (
            <SkipOrderLink
              order={get(upcomingOrders, 'upcomingOrder', null)}
              orderDate={orderDate}
              nextOrderDate={nextOrderDate}
            />
          )}
        </div>
       {paymentFailure && <PaymentErrorMessageComponent />}
        {hasRestrictedItems && (
          <UserMessage
            className={ageRestrictedMsgStyle}
            messageText={appLabels.UserWithActiveOrders.hasRestrictedItems}
            messageType={'warning'}
          />
        )}

        {displaySlotChangesMessage ? (
          <UserMessage
            className={messageStyle}
            messageText="Changes made apply to this order only. To change ongoing subscriptions, edit your scheduled items."
            messageType="info"
          />
        ) : null}

        {!isEmpty(firstTabBannerMessage) ? (
          <UserMessage className={messageStyle} messageText={firstTabBannerMessage} messageType="warning" />
        ) : null}

        {isApiLoading ? (
          <OrderTile upcomingOrders={{}} fillWidth={true} expanded={false} loading={true} />
        ) : (
          <div>
            {isApiError || !upcomingOrder ? (
              <UserMessage messageText={appLabels.ApiErrorMessages.activeOrders} messageType={'warning'} />
            ) : (
              <OrderTile upcomingOrders={upcomingOrders} fillWidth={true} preferencesData={preferencesData} />
            )}
          </div>
        )}
      </div>
      <div className={secondSectionStyle}>
        <div className={futureSectionStyle}>
          <div className={futureTextContainer}>
            <div className={titleStyle}>{appLabels.UserWithActiveOrders.futureText}</div>
            {hasFutureOrders && <div className={subTitleStyle}>{showNextOrderText}</div>}
          </div>
          {isFutureApiError || !hasFutureOrders ? null : (
            <div className={futureExpandButtonContainer}>
              <ExpandOrderButton
                expanded={expanded}
                onChange={(value) => {
                  setExpanded(value);
                  setExpandedFutureOrder(-1);
                }}
              />
            </div>
          )}
        </div>
        {isFutureApiLoading ? (
          <div className={futureOrderListStyle}>
            {range(3).map((a, index) => (
              <OrderTile key={index} upcomingOrders={{}} fillWidth={true} expanded={false} loading={true} />
            ))}
          </div>
        ) : (
          <div className={futureOrderListStyle}>
            {isFutureApiError || !hasFutureOrders ? (
              <UserMessage messageText={appLabels.ApiErrorMessages.activeOrders} messageType={'warning'} />
            ) : (
              expanded &&
              futureOrderList.map((order, i) => {
                return (
                  <div className={futureOrderStyle} key={`futureOrder_${i}`}>
                    <OrderTile
                      upcomingOrders={order}
                      fillWidth={true}
                      expanded={i === expandedFutureOrder}
                      onExpand={curry(handleFutureOrderExpand)(i)}
                    />
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

UserWithActiveOrders.propTypes = {
  upcomingOrders: PropTypes.object,
  futureOrders: PropTypes.object,
  preferencesData: PropTypes.object,
  isApiError: PropTypes.bool,
  isFutureApiError: PropTypes.bool,
  isApiLoading: PropTypes.bool,
  isFutureApiLoading: PropTypes.bool,
  hasRestrictedItems: PropTypes.bool,
};

export default UserWithActiveOrders;
